import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { operationsAPI, usersAPI, hrAPI, logisticsAPI, formatError } from "../utils/api";
import { useKitchen } from "../contexts/KitchenContext";
import { Plus, X, Camera, Upload, MapPin, Truck, Package, BarChart3, Search, Download, FileSpreadsheet, Printer, Bell } from "lucide-react";
import { exportCSV } from "../utils/csvUtils";
import { exportPayrollExcel, printPayrollPDF } from "../utils/payrollExport";

const TRAY_TYPES = [{ value: "egg_tray", label: "Egg Trays" }, { value: "plastic_tray", label: "Plastic Trays" }];

function Modal({ title, onClose, children, wide = false }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className={`bg-white rounded-2xl shadow-xl w-full ${wide ? "max-w-2xl" : "max-w-lg"} max-h-[92vh] overflow-y-auto animate-fade-in-up`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DB] sticky top-0 bg-white">
          <h2 className="font-bold text-lg font-[Manrope] text-[#3A322C]">{title}</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function ImageUpload({ images, onChange, label }) {
  const ref = useRef();
  const add = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = ev => onChange([...images, ev.target.result]);
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };
  return (
    <div>
      <label className="text-xs font-bold uppercase text-[#8A7E73] mb-2 block">{label}</label>
      <div className="flex flex-wrap gap-2">
        {images.map((img, i) => (
          <div key={`item-${i}`} className="relative">
            <img src={img} alt="" className="w-20 h-20 object-cover rounded-lg border border-[#E5E1DB]" />
            <button onClick={() => onChange(images.filter((_, j) => j !== i))}
              className="absolute -top-1 -right-1 w-5 h-5 bg-[#C2624E] text-white rounded-full text-xs flex items-center justify-center">✕</button>
          </div>
        ))}
        <button onClick={() => ref.current.click()}
          className="w-20 h-20 border-2 border-dashed border-[#E5E1DB] rounded-lg flex flex-col items-center justify-center text-[#8A7E73] hover:border-[#E8956D] hover:text-[#E8956D] transition-colors">
          <Camera size={18} /><span className="text-[10px] mt-1">Add</span>
        </button>
      </div>
      <input ref={ref} type="file" accept="image/*" multiple className="hidden" onChange={add} />
    </div>
  );
}

const TABS = [
  { id: "field", label: "Field Visits", icon: MapPin },
  { id: "vehicles", label: "Vehicle Usage", icon: Truck },
  { id: "trays", label: "Tray Inventory", icon: Package },
  { id: "ot", label: "OT Monthly Report", icon: BarChart3 },
];

export default function OperationsExtra() {
  const [searchParams] = useSearchParams();
  const { kitchens: ctxKitchens, selectedKitchen, changeKitchen } = useKitchen();
  const kitchenCtx = useKitchen();
  const [kitchens, setKitchens] = useState(ctxKitchens || []);
  // Fallback: direct fetch if context kitchens still empty
  useEffect(() => {
    if (ctxKitchens && ctxKitchens.length > 0) { setKitchens(ctxKitchens); return; }
    const _t = setTimeout(() => {
      if (kitchens.length > 0) return;
      import("../utils/api").then(({ usersAPI }) => {
        usersAPI.getKitchens().then(r => {
          const ks = r.data || [];
          if (ks.length > 0) setKitchens(ks);
        }).catch(() => {});
      });
    }, 800);
    return () => clearTimeout(_t);
  }, [ctxKitchens]);

  const [activeTab, setActiveTab] = useState("field");
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t) setActiveTab(t);
  }, [searchParams]);
  const [fieldVisits, setFieldVisits] = useState([]);
  const [vehicleUsage, setVehicleUsage] = useState([]);
  const [vehicleReport, setVehicleReport] = useState(null);
  const [trayInventory, setTrayInventory] = useState([]);
  const [trayMovements, setTrayMovements] = useState([]);
  const [otReport, setOtReport] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`; });
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [formImages, setFormImages] = useState([]);
  const [formSchools, setFormSchools] = useState([""]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [search, setSearch] = useState("");
  const [otApprovalModal, setOtApprovalModal] = useState(null);
  const [otApprovalForm, setOtApprovalForm] = useState({});
  const [otRate, setOtRate] = useState(0); // OT rate per hour in ₹

  useEffect(() => {
    // Load kitchens into context — but DON'T override user's selection
    usersAPI.getKitchens().then(r => {
      const ks = r.data || [];
      if (ks.length > 0 && kitchenCtx?.loadKitchens) kitchenCtx.loadKitchens(ks);
    });
    hrAPI.getDepartments().then(r => setDepartments(r.data || [])).catch(() => {});
    logisticsAPI.getVehicles().then(r => setVehicles(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { if (selectedKitchen) fetchTab(); }, [activeTab, selectedKitchen, selectedMonth]);

  const fetchTab = async () => {
    setLoading(true);
    try {
      if (activeTab === "field") {
        const r = await operationsAPI.getFieldVisits({ kitchen_id: selectedKitchen, limit: 100 });
        setFieldVisits(r.data || []);
      } else if (activeTab === "vehicles") {
        const today = new Date();
        const dateFrom = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,"0")}-01`;
        const [logs, report] = await Promise.all([
          operationsAPI.getVehicleUsage({ kitchen_id: selectedKitchen, date_from: dateFrom, limit: 60 }),
          operationsAPI.getVehicleReport({ kitchen_id: selectedKitchen, date_from: dateFrom })
        ]);
        setVehicleUsage(logs.data || []);
        setVehicleReport(report.data);
      } else if (activeTab === "trays") {
        const [inv, mov] = await Promise.all([
          operationsAPI.getTrayInventory({ kitchen_id: selectedKitchen }),
          operationsAPI.getTrayMovements({ kitchen_id: selectedKitchen, limit: 50 })
        ]);
        setTrayInventory(inv.data || []);
        setTrayMovements(mov.data || []);
      } else if (activeTab === "ot") {
        const [year, month] = selectedMonth.split("-").map(Number);
        const r = await operationsAPI.getMonthlyReport({ kitchen_id: selectedKitchen, year, month });
        setOtReport(r.data);
      }
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setLoading(false);
  };

  const handleSaveField = async () => {
    setError("");
    try {
      const payload = {
        ...form,
        kitchen_id: selectedKitchen,
        schools_visited: formSchools.filter(s => s.trim()),
        feedback_images: formImages,
      };
      await operationsAPI.createFieldVisit(payload);
      setModal(null); setForm({}); setFormImages([]); setFormSchools([""]);
      setSuccess("Visit logged!"); setTimeout(() => setSuccess(""), 2000);
      fetchTab();
    } catch (e) { setError(formatError(e)); }
  };

  const handleSaveVehicle = async () => {
    setError("");
    try {
      await operationsAPI.logVehicleUsage({ ...form, kitchen_id: selectedKitchen });
      setModal(null); setForm({});
      setSuccess("Logged!"); setTimeout(() => setSuccess(""), 2000);
      fetchTab();
    } catch (e) { setError(formatError(e)); }
  };

  const handleSaveTray = async () => {
    setError("");
    try {
      await operationsAPI.addTrayMovement({ ...form, kitchen_id: selectedKitchen });
      setModal(null); setForm({});
      setSuccess("Movement recorded!"); setTimeout(() => setSuccess(""), 2000);
      fetchTab();
    } catch (e) { setError(formatError(e)); }
  };

  const handleApproveOT = async () => {
    try {
      await operationsAPI.approveOT(otApprovalModal.record_id, otApprovalForm);
      setOtApprovalModal(null); setOtApprovalForm({});
      fetchTab();
    } catch (e) { alert(formatError(e)); }
  };

  const filteredVisits = fieldVisits.filter(v => !search || v.employee_name?.toLowerCase().includes(search.toLowerCase()) || v.work_location?.toLowerCase().includes(search.toLowerCase()));

  const getTrayBalance = (type) => {
    const item = trayInventory.find(t => t.tray_type === type && t.kitchen_id === selectedKitchen);
    return item?.balance || 0;
  };

  return (
    <div className="space-y-6 max-w-6xl" data-testid="operations-module">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold font-[Manrope] text-[#3A322C]">Operations Tracking</h1>
          <p className="text-sm text-[#8A7E73] mt-0.5">Field visits, vehicle usage, tray inventory & OT reports</p>
        </div>
        <div className="flex items-center gap-2">
          {activeTab === "ot" && (
            <input type="month" className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
              value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} />
          )}
        </div>
      </div>

      {success && <div className="bg-[#EDF2EE] border border-[#637C64]/30 rounded-lg px-4 py-2.5 text-sm text-[#637C64]">{success}</div>}

      <div className="flex border-b border-[#E5E1DB] overflow-x-auto">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-4 py-3 text-sm font-semibold flex items-center gap-2 whitespace-nowrap transition-colors -mb-px ${activeTab === t.id ? "border-b-2 border-[#E8956D] text-[#E8956D]" : "text-[#8A7E73] hover:text-[#5C5147]"}`}>
            <t.icon size={15} />{t.label}
          </button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-12"><div className="w-8 h-8 border-3 border-[#E8956D] border-t-transparent rounded-full animate-spin" /></div> : (
        <>
          {/* ── FIELD VISITS ── */}
          {activeTab === "field" && (
            <div className="space-y-4">
              <div className="flex gap-3 items-center flex-wrap">
                <div className="relative flex-1 min-w-48">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A7E73]" />
                  <input placeholder="Search visits..." value={search} onChange={e => setSearch(e.target.value)}
                    className="w-full border border-[#E5E1DB] rounded-lg pl-8 pr-3 py-2 text-sm outline-none focus:border-[#E8956D]" />
                </div>
                <button onClick={() => exportCSV(filteredVisits.map(v => ({ date: v.date, employee: v.employee_name, work: v.nature_of_work, location: v.work_location, schools: v.schools_visited?.join("; "), notes: v.notes })), "field_visits")}
                  className="flex items-center gap-1.5 text-sm border border-[#E5E1DB] rounded-lg px-3 py-2 text-[#5C5147] hover:bg-[#F4F3F0]"><Download size={14} /> Export</button>
                <button onClick={() => { setForm({ date: new Date().toISOString().split("T")[0] }); setFormImages([]); setFormSchools([""]); setModal("field"); setError(""); }}
                  className="bg-[#E8956D] text-[#3A322C] rounded-lg px-4 py-2 text-sm font-bold flex items-center gap-1.5"><Plus size={14} /> Log Visit</button>
              </div>
              <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F4F3F0]">
                      <tr>{["Date", "Employee", "Nature of Work", "Location", "Schools Visited", "Images", "Notes"].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-[#5C5147]">{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody className="divide-y divide-[#F4F3F0]">
                      {filteredVisits.map(v => (
                        <tr key={v.id} className="hover:bg-[#FAFAF8]">
                          <td className="px-4 py-3 text-[#8A7E73] whitespace-nowrap">{v.date}</td>
                          <td className="px-4 py-3 font-medium text-[#3A322C]">{v.employee_name || v.created_by_name || "—"}</td>
                          <td className="px-4 py-3 text-[#5C5147]">{v.nature_of_work}</td>
                          <td className="px-4 py-3 text-[#8A7E73]">{v.work_location}</td>
                          <td className="px-4 py-3">
                            {v.schools_visited?.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {v.schools_visited.slice(0, 2).map((s, i) => <span key={`idx-${i}`} className="text-xs bg-[#F4F3F0] text-[#5C5147] px-2 py-0.5 rounded-full">{s}</span>)}
                                {v.schools_visited.length > 2 && <span className="text-xs text-[#8A7E73]">+{v.schools_visited.length - 2} more</span>}
                              </div>
                            ) : "—"}
                          </td>
                          <td className="px-4 py-3 text-[#8A7E73]">{v.image_count || 0} photos</td>
                          <td className="px-4 py-3 text-[#8A7E73] text-xs">{v.notes || "—"}</td>
                        </tr>
                      ))}
                      {filteredVisits.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-[#8A7E73]">No field visits logged</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ── VEHICLE USAGE ── */}
          {activeTab === "vehicles" && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <button onClick={() => { setForm({ date: new Date().toISOString().split("T")[0], regular_vehicles: [], additional_vehicles: [] }); setModal("vehicle"); setError(""); }}
                  className="bg-[#E8956D] text-[#3A322C] rounded-lg px-4 py-2 text-sm font-bold flex items-center gap-1.5"><Plus size={14} /> Log Today's Vehicles</button>
              </div>
              {vehicleReport && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: "Days Tracked", value: vehicleReport.total_days, color: "text-[#3A322C]" },
                    { label: "Regular Deployments", value: vehicleReport.total_regular_deployments, color: "text-[#637C64]" },
                    { label: "Additional Deployments", value: vehicleReport.total_additional_deployments, color: "text-[#D98032]" },
                    { label: "Avg Vehicles/Day", value: vehicleReport.avg_vehicles_per_day, color: "text-[#E8956D]" },
                  ].map(s => (
                    <div key={s.label} className="bg-white border border-[#E5E1DB] rounded-xl p-4 text-center">
                      <p className={`text-2xl font-extrabold font-[Manrope] ${s.color}`}>{s.value}</p>
                      <p className="text-xs text-[#8A7E73] mt-1 font-medium">{s.label}</p>
                    </div>
                  ))}
                </div>
              )}
              <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F4F3F0]">
                      <tr>{["Date", "Regular Vehicles", "Additional/Temp Vehicles", "Total", "Notes"].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-[#5C5147]">{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody className="divide-y divide-[#F4F3F0]">
                      {vehicleUsage.map(v => (
                        <tr key={v.id} className="hover:bg-[#FAFAF8]">
                          <td className="px-4 py-3 text-[#8A7E73] whitespace-nowrap">{v.date}</td>
                          <td className="px-4 py-3 text-[#5C5147]">{v.regular_vehicles?.join(", ") || "—"}</td>
                          <td className="px-4 py-3 text-[#D98032]">{v.additional_vehicles?.join(", ") || "—"}</td>
                          <td className="px-4 py-3 font-bold text-[#3A322C]">{v.total_vehicles}</td>
                          <td className="px-4 py-3 text-[#8A7E73] text-xs">{v.notes || "—"}</td>
                        </tr>
                      ))}
                      {vehicleUsage.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-[#8A7E73]">No vehicle usage logged</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ── TRAY INVENTORY ── */}
          {activeTab === "trays" && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <button onClick={() => { setForm({ tray_type: "egg_tray", movement: "in", quantity: 0, date: new Date().toISOString().split("T")[0] }); setModal("tray"); setError(""); }}
                  className="bg-[#E8956D] text-[#3A322C] rounded-lg px-4 py-2 text-sm font-bold flex items-center gap-1.5"><Plus size={14} /> Add Movement</button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {TRAY_TYPES.map(t => (
                  <div key={t.value} className="bg-white border border-[#E5E1DB] rounded-xl p-5 text-center">
                    <p className="text-xs font-bold uppercase text-[#8A7E73] mb-2">{t.label}</p>
                    <p className="text-4xl font-extrabold font-[Manrope] text-[#E8956D]">{getTrayBalance(t.value)}</p>
                    <p className="text-xs text-[#8A7E73] mt-1">Current Balance</p>
                  </div>
                ))}
              </div>
              <h3 className="font-bold text-[#3A322C]">Recent Movements</h3>
              <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F4F3F0]">
                      <tr>{["Date", "Tray Type", "Movement", "Quantity", "By", "Notes"].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-[#5C5147]">{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody className="divide-y divide-[#F4F3F0]">
                      {trayMovements.map(m => (
                        <tr key={m.id} className="hover:bg-[#FAFAF8]">
                          <td className="px-4 py-3 text-[#8A7E73]">{m.date}</td>
                          <td className="px-4 py-3 text-[#5C5147]">{TRAY_TYPES.find(t => t.value === m.tray_type)?.label || m.tray_type}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${m.movement === "in" ? "bg-[#EDF2EE] text-[#637C64]" : "bg-[#FBEAE7] text-[#C2624E]"}`}>
                              {m.movement === "in" ? "In" : "Out"}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-bold">{m.quantity}</td>
                          <td className="px-4 py-3 text-[#8A7E73]">{m.created_by_name || "—"}</td>
                          <td className="px-4 py-3 text-[#8A7E73] text-xs">{m.notes || "—"}</td>
                        </tr>
                      ))}
                      {trayMovements.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-[#8A7E73]">No movements recorded</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ── OT MONTHLY REPORT ── */}
          {activeTab === "ot" && otReport && (
            <div className="space-y-4">
              {/* OT Rate + Export Controls */}
              <div className="bg-white border border-[#E5E1DB] rounded-xl p-4 flex flex-wrap items-end gap-4">
                <div>
                  <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">OT Rate per Hour (₹) — for payroll</label>
                  <input type="number" step="0.5" min="0" placeholder="0 = not included"
                    className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D] w-48"
                    value={otRate || ""} onChange={e => setOtRate(parseFloat(e.target.value) || 0)} />
                </div>
                <div className="flex gap-2 ml-auto">
                  <button
                    onClick={() => exportPayrollExcel({ report: otReport, kitchenName: kitchens.find(k => k.id === selectedKitchen)?.name || "Kitchen", otRatePerHour: otRate })}
                    className="flex items-center gap-2 bg-[#637C64] text-white rounded-lg px-4 py-2.5 text-sm font-bold hover:bg-[#4e6350]">
                    <FileSpreadsheet size={15} /> Export Excel
                  </button>
                  <button
                    onClick={() => printPayrollPDF({ report: otReport, kitchenName: kitchens.find(k => k.id === selectedKitchen)?.name || "Kitchen", otRatePerHour: otRate })}
                    className="flex items-center gap-2 bg-[#5C5147] text-white rounded-lg px-4 py-2.5 text-sm font-bold hover:bg-[#4A4038]">
                    <Printer size={15} /> Print / PDF
                  </button>
                  <button onClick={() => exportCSV((otReport.staff || []).map(s => ({ name: s.staff_name, employee_id: s.employee_id, days_present: s.days_present, total_hours: s.total_hours, ot_hours: s.ot_hours, late_days: s.late_days })), `ot_report_${selectedMonth}`)}
                    className="flex items-center gap-1.5 text-sm border border-[#E5E1DB] rounded-lg px-3 py-2 text-[#5C5147] hover:bg-[#F4F3F0]"><Download size={14} /> CSV</button>
                </div>
              </div>
              <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F4F3F0]">
                      <tr>{["Name", "Emp ID", "Days Present", "Total Hours", "OT Hours", "Late Days", ""].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-[#5C5147]">{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody className="divide-y divide-[#F4F3F0]">
                      {(otReport.staff || []).map(s => (
                        <tr key={s.staff_id} className="hover:bg-[#FAFAF8]">
                          <td className="px-4 py-3 font-medium text-[#3A322C]">{s.staff_name}</td>
                          <td className="px-4 py-3 text-[#8A7E73]">{s.employee_id}</td>
                          <td className="px-4 py-3 font-bold text-[#637C64]">{s.days_present}</td>
                          <td className="px-4 py-3 font-bold">{s.total_hours}h</td>
                          <td className="px-4 py-3 font-bold text-[#D98032]">{s.ot_hours}h</td>
                          <td className="px-4 py-3">{s.late_days > 0 ? <span className="text-xs bg-[#FDF4EB] text-[#D98032] px-2 py-0.5 rounded-full font-bold">{s.late_days}</span> : "0"}</td>
                          <td className="px-4 py-3">
                            {s.ot_hours > 0 && (
                              <button onClick={() => { setOtApprovalModal({ staff_name: s.staff_name, records: s.records, record_id: s.records?.[0]?.date }); setOtApprovalForm({}); }}
                                className="text-xs bg-[#E8956D]/10 text-[#E8956D] px-2 py-1 rounded font-bold hover:bg-[#E8956D]/20">Approve OT</button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {(otReport.staff || []).length === 0 && <tr><td colSpan={7} className="text-center py-10 text-[#8A7E73]">No attendance data for this month</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* FIELD VISIT MODAL */}
      {modal === "field" && (
        <Modal title="Log Field Visit / Outside Duty" onClose={() => setModal(null)} wide>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Date *</label><input type="date" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.date || ""} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Employee Name</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.employee_name || ""} onChange={e => setForm({ ...form, employee_name: e.target.value })} /></div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Nature of Work *</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" placeholder="e.g. School delivery, Coordination..." value={form.nature_of_work || ""} onChange={e => setForm({ ...form, nature_of_work: e.target.value })} /></div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Work Location *</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" placeholder="Area / District" value={form.work_location || ""} onChange={e => setForm({ ...form, work_location: e.target.value })} /></div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-bold uppercase text-[#8A7E73]">Schools Visited</label>
                <button onClick={() => setFormSchools([...formSchools, ""])} className="text-xs text-[#E8956D] font-bold">+ Add School</button>
              </div>
              <div className="space-y-2">
                {formSchools.map((s, i) => (
                  <div key={`item-${i}`} className="flex gap-2">
                    <input className="flex-1 border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                      placeholder={`School ${i + 1} name`} value={s} onChange={e => { const arr = [...formSchools]; arr[i] = e.target.value; setFormSchools(arr); }} />
                    {formSchools.length > 1 && <button onClick={() => setFormSchools(formSchools.filter((_, j) => j !== i))} className="text-[#C4BAAF] hover:text-[#C2624E]"><X size={16} /></button>}
                  </div>
                ))}
              </div>
            </div>
            <ImageUpload images={formImages} onChange={setFormImages} label="Teacher Feedback Photos (upload images)" />
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Notes</label><textarea rows={2} className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D] resize-none" value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            {error && <p className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{error}</p>}
            <div className="flex gap-3"><button onClick={() => setModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button><button onClick={handleSaveField} className="flex-1 bg-[#E8956D] rounded-lg py-2.5 text-sm font-bold text-[#3A322C]">Save Visit</button></div>
          </div>
        </Modal>
      )}

      {/* VEHICLE USAGE MODAL */}
      {modal === "vehicle" && (
        <Modal title="Log Vehicle Usage" onClose={() => setModal(null)}>
          <div className="space-y-4">
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Date *</label><input type="date" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.date || ""} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-2 block">Regular Vehicles (used today)</label>
              <div className="max-h-40 overflow-y-auto border border-[#E5E1DB] rounded-lg p-2 space-y-1">
                {vehicles.map(v => (
                  <label key={v.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={(form.regular_vehicles || []).includes(v.vehicle_number)}
                      onChange={e => {
                        const arr = form.regular_vehicles || [];
                        setForm({ ...form, regular_vehicles: e.target.checked ? [...arr, v.vehicle_number] : arr.filter(x => x !== v.vehicle_number) });
                      }} />
                    {v.vehicle_number} ({v.vehicle_type})
                  </label>
                ))}
              </div>
            </div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Additional/Temp Vehicles (comma separated)</label>
              <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" placeholder="e.g. TS07AB1234, TS09CD5678"
                onChange={e => setForm({ ...form, additional_vehicles: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} /></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Notes</label><textarea rows={2} className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D] resize-none" value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            {error && <p className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{error}</p>}
            <div className="flex gap-3"><button onClick={() => setModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button><button onClick={handleSaveVehicle} className="flex-1 bg-[#E8956D] rounded-lg py-2.5 text-sm font-bold text-[#3A322C]">Save</button></div>
          </div>
        </Modal>
      )}

      {/* TRAY MOVEMENT MODAL */}
      {modal === "tray" && (
        <Modal title="Record Tray Movement" onClose={() => setModal(null)}>
          <div className="space-y-4">
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Tray Type *</label>
              <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.tray_type || "egg_tray"} onChange={e => setForm({ ...form, tray_type: e.target.value })}>
                {TRAY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Movement *</label>
              <div className="flex gap-3">
                {["in", "out"].map(m => (
                  <button key={m} type="button" onClick={() => setForm({ ...form, movement: m })}
                    className={`flex-1 py-2.5 rounded-lg text-sm font-bold border-2 capitalize transition-all ${form.movement === m ? (m === "in" ? "border-[#637C64] bg-[#EDF2EE] text-[#637C64]" : "border-[#C2624E] bg-[#FBEAE7] text-[#C2624E]") : "border-[#E5E1DB] text-[#5C5147]"}`}>
                    {m === "in" ? "Stock In" : "Stock Out"}
                  </button>
                ))}
              </div></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Quantity *</label><input type="number" min="1" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.quantity || ""} onChange={e => setForm({ ...form, quantity: parseInt(e.target.value) || 0 })} /></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Date</label><input type="date" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.date || new Date().toISOString().split("T")[0]} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Notes</label><input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            {error && <p className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{error}</p>}
            <div className="flex gap-3"><button onClick={() => setModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button><button onClick={handleSaveTray} className="flex-1 bg-[#E8956D] rounded-lg py-2.5 text-sm font-bold text-[#3A322C]">Record</button></div>
          </div>
        </Modal>
      )}

      {/* OT APPROVAL MODAL */}
      {otApprovalModal && (
        <Modal title={`OT Approval — ${otApprovalModal.staff_name}`} onClose={() => setOtApprovalModal(null)}>
          <div className="space-y-4">
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Reason for OT *</label><textarea rows={2} className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D] resize-none" value={otApprovalForm.reason || ""} onChange={e => setOtApprovalForm({ ...otApprovalForm, reason: e.target.value })} /></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Work Assigned During OT *</label><textarea rows={2} className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D] resize-none" value={otApprovalForm.work_assigned || ""} onChange={e => setOtApprovalForm({ ...otApprovalForm, work_assigned: e.target.value })} /></div>
            <div className="flex gap-3"><button onClick={() => setOtApprovalModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button><button onClick={handleApproveOT} className="flex-1 bg-[#637C64] text-white rounded-lg py-2.5 text-sm font-bold">Approve OT</button></div>
          </div>
        </Modal>
      )}
    </div>
  );
}
